import React, { useEffect, useState } from "react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { Button, Card, CardBody, Col, Container, Input, Row } from "reactstrap";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import { Triangle } from "react-loader-spinner";
import QuizService from "../../../services/KangarooServices/QuizService";
import { formatDate } from "../../../Components/Common/FormatDate";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import "flatpickr/dist/flatpickr.min.css";

const customStyles = {
  headCells: {
    style: {
      fontSize: "0.9rem",
      fontWeight: 600,
    },
  },
  rows: {
    style: {
      fontSize: "0.9rem",
    },
  },
};

const QuizLog = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    searchText: "",
    fromDate: "",
    toDate: "",
  });

  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // ✅ Debounce filters
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [filters]);

  // ✅ Fetch data (server-side)
  const fetchData = async () => {
    try {
      setLoading(true);

      const result = await QuizService.getLogs({
        pageNumber: currentPage,
        pageSize: pageSize,
        searchText: debouncedFilters.searchText || undefined,
        fromDate: debouncedFilters.fromDate || undefined,
        toDate: debouncedFilters.toDate || undefined,
      });

      const items = result?.items || result?.data?.items || [];

      const total = result?.totalCount || result?.data?.totalCount || 0;

      setData(Array.isArray(items) ? items : []);
      setTotalCount(total);
    } catch (error) {
      console.log(error);
      setData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Trigger API on page / filter change
  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, debouncedFilters]);

  // ✅ Columns
  const columns = [
    {
      name: "S. N",
      cell: (row, index) => (
        <div>{(currentPage - 1) * pageSize + index + 1}</div>
      ),
      width: "75px",
      center: true,
    },
    {
      name: "Quiz",
      selector: (row) => row.quizName,
      cell: (row) => (
        <span
          title={row.quizName}
          onClick={() => navigate(`/quiz-log/${row.id}`)}
          className="text-secondary cursor-pointer text-truncate"
        >
          {row.quizName}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Submitted",
      selector: (row) => row.submittedAt,
      cell: (row) => formatDate(row.submittedAt),
      sortable: true,
    },
    {
      name: "Username",
      selector: (row) => row.userName,
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.userEmail,
      cell: (row) => (
        <span title={row.userEmail} className="text-truncate">
          {row.userEmail}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Question",
      selector: (row) => row.totalQuestions,
      sortable: true,
      right: true,
    },
    {
      name: "Correct",
      selector: (row) => row.correctAnswers,
      sortable: true,
      right: true,
    },
    {
      name: "Score(%)",
      selector: (row) => row.scorePercentage,
      sortable: true,
      right: true,
    },
  ];

  // ✅ Export (current page only)
  const handleExport = () => {
    if (!data || data.length === 0) return;

    const formattedData = data.map((row, index) => ({
      "S.N": (currentPage - 1) * pageSize + index + 1,
      Quiz: row.quizName,
      Submitted: formatDate(row.submittedAt),
      Username: row.userName,
      Email: row.userEmail,
      Questions: row.totalQuestions,
      Correct: row.correctAnswers,
      "Score (%)": row.scorePercentage,
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Quiz Logs");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const file = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    saveAs(file, "Quiz_Logs.xlsx");
  };

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb title="Quiz Logs" pageTitle="Course Management" />

        {/* ✅ Filters */}
        <Row className="mb-3">
          <Col lg={4}>
            <Input
              placeholder="Search"
              value={filters.searchText}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  searchText: e.target.value,
                }))
              }
              className="w-100 "
            />
          </Col>

          <Col>
            <Flatpickr
              className="form-control"
              options={{
                mode: "range",
                dateFormat: "Y-m-d",
              }}
              value={
                filters.fromDate && filters.toDate
                  ? [filters.fromDate, filters.toDate]
                  : null
              }
              placeholder="Pick date range..."
              onChange={(dates) => {
                if (dates.length === 0) {
                  setFilters((prev) => ({
                    ...prev,
                    fromDate: "",
                    toDate: "",
                  }));
                  return;
                }

                const [start, end] = dates;

                setFilters((prev) => ({
                  ...prev,
                  fromDate: start ? start.toISOString().split("T")[0] : "",
                  toDate: end ? end.toISOString().split("T")[0] : "",
                }));
              }}
            />
          </Col>

          <Col>
            <div className="text-end">
              <Button color="success" onClick={handleExport}>
                <i className="ri-file-excel-2-fill me-1" />
                Export
              </Button>
            </div>
          </Col>
        </Row>

        {/* ✅ Table */}
        <Card>
          <CardBody>
            <CustomDataTable
              columns={columns}
              data={data}
              customStyles={customStyles}
              pagination
              paginationServer
              paginationTotalRows={totalCount}
              paginationPerPage={pageSize}
              onChangeRowsPerPage={(newPerPage) => {
                setPageSize(newPerPage);
                setCurrentPage(1);
              }}
              onChangePage={(page) => {
                setCurrentPage(page);
              }}
              striped
              responsive
              fixedHeader
              progressPending={loading}
              progressComponent={
                <div className="my-3 text-center">
                  <Triangle
                    visible={true}
                    height="80"
                    width="80"
                    color="#5B71B9"
                  />
                  <h5 className="mt-1">Loading...</h5>
                </div>
              }
            />
          </CardBody>
        </Card>
      </div>
    </Container>
  );
};

export default QuizLog;
